import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { isDefined } from '../../utils/variables';
import { errorUtils, translateApiErrors, TranslatedError } from '../api/error-handling';
import { Button } from '../components/button/button';
import { InfoBox, InfoBoxProps } from '../components/info-box/info-box';
import { List } from '../components/list/list';
import { Stack } from '../components/stack/stack';

type ErrorFilter<T> = (err: TranslatedError<T>) => boolean;
type RetryCallback = () => any;

/**
 * Returns `prev` when its content matches `next`, otherwise returns `next`.
 * Pass as a functional updater to `setTranslatedErrorMessages` to preserve the existing
 * reference when errors are unchanged, avoiding the re-render cascade that a new array
 * reference would trigger.
 * @example
 * setTranslatedErrorMessages((prev) => stableErrors(prev, translateApiErrors(apiError)));
 */
function stableErrors<T>(
  prev: TranslatedError<T>[],
  next: TranslatedError<T>[],
): TranslatedError<T>[] {
  if (
    prev.length === next.length &&
    prev.every((e, i) => e.path === next[i].path && e.errorMessage === next[i].errorMessage)
  ) {
    return prev;
  }
  return next;
}

export type ErrorOrDefaultOptions<T> = {
  filter?: ErrorFilter<T>;
  defaultMessage?: string;
};

type InfoBoxOptions = {
  infoBoxProps?: Partial<Pick<InfoBoxProps, 'bottomMargin' | 'topMargin' | 'width'>>;
};

export function useErrorHandling<T>({ displayNonApiErrors } = { displayNonApiErrors: false }) {
  const { t } = useTranslation();
  const defaultErrorMessage = t('unexpectedErrorWithSupport', { ns: 'common' });
  const [translatedErrorMessages, setTranslatedErrorMessages] = useState<TranslatedError<T>[]>([]);
  const [nonApiError, setNonApiError] = useState<Error>();
  const [retryCallback, setRetryCallback] = useState<RetryCallback>();

  const excludeErrorPaths = useCallback((paths: T[]) => {
    return (error: TranslatedError<T>) => !paths.includes(error.path);
  }, []);

  const includeErrorPaths = useCallback((paths: T[]) => {
    return (error: TranslatedError<T>) => paths.includes(error.path);
  }, []);

  const getError = useCallback(
    (filter: ErrorFilter<T> = () => true) => {
      const fallback = displayNonApiErrors ? nonApiError?.message : undefined;
      return translatedErrorMessages.filter(filter).at(0)?.errorMessage ?? fallback;
    },
    [nonApiError, translatedErrorMessages, displayNonApiErrors],
  );

  const getErrors = useCallback(
    (filter: ErrorFilter<T> = () => true) => {
      const fallback = displayNonApiErrors && isDefined(nonApiError) ? [nonApiError?.message] : [];
      const errors = translatedErrorMessages.filter(filter).map((x) => x.errorMessage);
      return errors.length > 0 ? errors : fallback;
    },
    [nonApiError, translatedErrorMessages, displayNonApiErrors],
  );

  const getErrorsOrDefault = useCallback(
    (options?: ErrorOrDefaultOptions<T>) => {
      const errors = getErrors(options?.filter);
      return errors.length === 0 ? [options?.defaultMessage ?? defaultErrorMessage] : errors;
    },
    [getErrors, defaultErrorMessage],
  );

  const getErrorForPath = useCallback(
    (path: T) => {
      return getError(includeErrorPaths([path]));
    },
    [getError, includeErrorPaths],
  );

  const handle = useCallback(
    (err: unknown, options?: { retryCallback: RetryCallback }) => {
      const apiError = errorUtils.toApiErrorResponse(err);
      setRetryCallback(() => {
        return options?.retryCallback;
      });

      if (apiError) {
        setTranslatedErrorMessages((prev) => stableErrors(prev, translateApiErrors(apiError)));
        setNonApiError(undefined);
      } else {
        const error =
          err instanceof Error
            ? err
            : new Error(typeof err === 'string' ? err : defaultErrorMessage);
        setNonApiError(error);
        setTranslatedErrorMessages((prev) => stableErrors(prev, []));
      }
    },
    [defaultErrorMessage],
  );

  const clearErrors = useCallback((filter?: ErrorFilter<T>) => {
    // handleError keeps translatedErrorMessages and nonApiError mutually exclusive,
    // so clearing both unconditionally is safe. Unnecessary setState calls are
    // prevented by stableErrors (for messages) and React's Object.is bail-out
    // (for nonApiError when it is already undefined).
    if (filter === undefined) {
      setTranslatedErrorMessages((prev) => stableErrors(prev, []));
      setNonApiError(undefined);
    } else {
      setTranslatedErrorMessages((prev) => stableErrors(prev, prev.filter(filter)));
      setNonApiError(undefined);
    }
  }, []);

  const hasApiError = useCallback(
    (filter?: ErrorFilter<T>) => {
      return getErrors(filter).length > 0;
    },
    [getErrors],
  );

  const hasError = useCallback(
    (filter: ErrorFilter<T> = () => true) => {
      return isDefined(nonApiError) || hasApiError(filter);
    },
    [nonApiError, hasApiError],
  );

  const clearErrorForPath = useCallback(
    (path: T) => {
      clearErrors(excludeErrorPaths([path]));
    },
    [clearErrors, excludeErrorPaths],
  );

  const buildErrorList = useCallback(
    (options?: ErrorOrDefaultOptions<T> & InfoBoxOptions & { retryCallback?: RetryCallback }) => {
      const errors = getErrorsOrDefault(options);
      return hasError() ? (
        <ErrorListInfoBox
          errorMessages={errors}
          infoBoxProps={options?.infoBoxProps}
          retryCallback={options?.retryCallback ?? retryCallback}
        />
      ) : undefined;
    },
    [getErrorsOrDefault, hasError, retryCallback],
  );

  const setApiError = useCallback(
    (path: T, errorMessage: string) => {
      translatedErrorMessages.push({
        errorMessage,
        path,
      });
    },
    [translatedErrorMessages],
  );

  return {
    handleError: handle,
    clearErrors,
    clearErrorForPath,
    getErrorForPath,
    buildErrorList,
    hasError,
    hasApiError,
    setApiError,
    excludeErrorPaths,
    includeErrorPaths,
    translatedErrorMessages,
  };
}

function ErrorListInfoBox(
  props: { errorMessages: string[]; retryCallback?: RetryCallback } & InfoBoxOptions,
) {
  const { t } = useTranslation();

  return (
    <InfoBox
      color="red"
      leftIcon={{ icon: ['fad', 'exclamation'] }}
      topMargin={true}
      {...props.infoBoxProps}
    >
      <Stack spacing={0.5}>
        <List items={props.errorMessages} />
        {isDefined(props.retryCallback) && (
          <Button
            color="primary"
            variant="contained"
            width="fit-content"
            onClick={async () => {
              await props.retryCallback?.();
            }}
          >
            {t('retry', { ns: 'common' })}
          </Button>
        )}
      </Stack>
    </InfoBox>
  );
}
